import React from 'react';
import {
  Box,
  Card,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AppIcon from '../common/AppIcon';
import { Document, downloadDocument } from '../../services/documents';
import { Topic } from '../../services/topics';

const getFileIcon = (filename: string, contentType: string) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (contentType === 'application/pdf' || ext === 'pdf') {
    return <AppIcon name="pdf" sx={{ color: '#E53935' }} />;
  }
  if (contentType.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext || '')) {
    return <AppIcon name="image" sx={{ color: '#7B1FA2' }} />;
  }
  if (contentType.includes('spreadsheet') || ['xlsx', 'xls', 'csv'].includes(ext || '')) {
    return <AppIcon name="table" sx={{ color: '#2E7D32' }} />;
  }
  if (contentType.includes('wordprocessing') || ['docx', 'doc'].includes(ext || '')) {
    return <AppIcon name="document" sx={{ color: '#1565C0' }} />;
  }
  return <AppIcon name="file" sx={{ color: '#B07A8F' }} />;
};

interface DocumentListProps {
  documents: Document[];
  topics: Topic[];
  onDelete: (doc: Document) => void;
  onTopicChange: (doc: Document) => void;
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const DocumentList: React.FC<DocumentListProps> = ({ documents, topics, onDelete, onTopicChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const topicMap = Object.fromEntries(topics.map((t) => [t.id, t]));

  const handleDownload = async (doc: Document) => {
    try {
      const res = await downloadDocument(doc.id);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed', err);
    }
  };

  if (documents.length === 0) {
    return (
      <Card>
        <Typography color="text.secondary">No documents yet. Upload your first file.</Typography>
      </Card>
    );
  }

  return (
    <List disablePadding>
      {documents.map((doc) => {
        const topic = doc.topic_id ? topicMap[doc.topic_id] : null;

        if (isMobile) {
          return (
            <ListItem
              key={doc.id}
              onClick={() => handleDownload(doc)}
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'grey.200',
                mb: 1,
                px: 2,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'grey.50' },
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                <Box sx={{ mt: 0.5 }}>
                  {getFileIcon(doc.filename, doc.content_type)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                    {doc.filename}
                  </Typography>
                  {topic && (
                    <Chip
                      label={`${topic.emoji ? `${topic.emoji} ` : ''}${topic.name}`}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        bgcolor: 'grey.100',
                        color: 'text.secondary',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        mt: 0.5,
                      }}
                    />
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mt: -0.5, pl: 4 }}>
                <Typography variant="caption" color="text.secondary">
                  {formatSize(doc.size_bytes)} — {new Date(doc.uploaded_at).toLocaleDateString('en-GB')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); onTopicChange(doc); }}>
                    <AppIcon name="topic" fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}>
                    <AppIcon name="download" fontSize="small" />
                  </IconButton>
                  <IconButton size="small" sx={{ color: 'error.main' }} onClick={(e) => { e.stopPropagation(); onDelete(doc); }}>
                    <AppIcon name="delete-outline" fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </ListItem>
          );
        }

        return (
          <ListItem
            key={doc.id}
            onClick={() => handleDownload(doc)}
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'grey.200',
              mb: 1,
              px: 3,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'grey.50' },
              '&:hover .action-btn': { opacity: 1 },
            }}
            secondaryAction={
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Change topic">
                  <IconButton
                    size="small"
                    className="action-btn"
                    sx={{ opacity: 0, transition: 'opacity 0.2s' }}
                    onClick={(e) => { e.stopPropagation(); onTopicChange(doc); }}
                  >
                    <AppIcon name="topic" fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download">
                  <IconButton
                    size="small"
                    className="action-btn"
                    sx={{ opacity: 0, transition: 'opacity 0.2s' }}
                    onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}
                  >
                    <AppIcon name="download" fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    className="action-btn"
                    sx={{ opacity: 0, transition: 'opacity 0.2s', color: 'error.main' }}
                    onClick={(e) => { e.stopPropagation(); onDelete(doc); }}
                  >
                    <AppIcon name="delete-outline" fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            }
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {getFileIcon(doc.filename, doc.content_type)}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                      flexShrink: 1,
                    }}
                  >
                    {doc.filename}
                  </Typography>
                  {topic && (
                    <Chip
                      label={`${topic.emoji ? `${topic.emoji} ` : ''}${topic.name}`}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        bgcolor: 'grey.100',
                        color: 'text.secondary',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Box>
              }
              secondary={`${formatSize(doc.size_bytes)} — ${new Date(doc.uploaded_at).toLocaleDateString('en-GB')}`}
              sx={{ mr: 1 }}
            />
          </ListItem>
        );
      })}
    </List>
  );
};

export default DocumentList;
